import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ComposedChart, Line } from "recharts";
import { gbp } from "@/components/analytics/AnalyticsParts";
import { Delta } from "@/components/ga/GaSections";

const num = (v) => (v == null ? "–" : Math.round(Number(v)).toLocaleString("en-GB"));
const pct = (v) => (v == null ? "–" : `${(v * 100).toFixed(2)}%`);
const x = (v) => (v == null ? "–" : `${Number(v).toFixed(2)}×`);
const money = (v) => (v == null ? "–" : gbp(v));

export function AdsKpis({ d }) {
  const t = d.ads?.totals, p = d.ads?.previous, rs = d.real_sales, rp = d.real_sales_previous;
  const cards = [
    ...(t ? [["Spend", money(t.cost), t.cost, p?.cost, true], ["Clicks", num(t.clicks), t.clicks, p?.clicks], ["Impressions", num(t.impressions), t.impressions, p?.impressions],
      ["CTR", pct(t.ctr), t.ctr, p?.ctr], ["Avg CPC", money(t.cpc), t.cpc, p?.cpc, true], ["Google conversions", num(t.conversions), t.conversions, p?.conversions],
      ["Cost / conversion", money(t.cpa), t.cpa, p?.cpa, true]] : []),
    ["Ads orders (WooCommerce)", num(rs.orders), rs.orders, rp?.orders], ["Ads revenue (WooCommerce)", money(rs.revenue), rs.revenue, rp?.revenue],
    ...(t ? [["Actual ROAS", x(t.actual_roas), t.actual_roas, p?.actual_roas], ["Cost per order", money(t.cost_per_order), t.cost_per_order, null, true]] : []),
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gads-kpis">
      {cards.map(([label, v, cur, prev, invert]) => (
        <div key={label} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4" data-testid={`gads-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{v}</div>
          <Delta cur={cur} prev={prev} invert={invert} />
        </div>
      ))}
    </div>
  );
}

export function CampaignChart({ d }) {
  const rows = d.ads ? d.ads.campaigns.slice(0, 12).map((c) => ({ name: c.campaign, Spend: c.cost, "WooCommerce revenue": c.wc_revenue }))
    : d.real_sales.by_campaign.slice(0, 12).map((c) => ({ name: c.campaign, "WooCommerce revenue": c.revenue }));
  if (!rows.length) return <p className="text-sm text-slate-400">No campaign data for this period.</p>;
  return (
    <div className="h-80" data-testid="gads-campaign-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
          <CartesianGrid stroke="#f1f5f9" /><XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${Math.round(v)}`} /><Tooltip formatter={(v) => gbp(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
          {d.ads && <Bar dataKey="Spend" fill="#94a3b8" radius={[3, 3, 0, 0]} />}<Bar dataKey="WooCommerce revenue" fill="#4f46e5" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AdsTrend({ daily }) {
  return (
    <div className="h-72" data-testid="gads-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={daily} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" /><XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} minTickGap={16} />
          <YAxis yAxisId="a" tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} /><YAxis yAxisId="b" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="a" dataKey="cost" name="Spend" fill="#c7d2fe" /><Line yAxisId="b" dataKey="clicks" name="Clicks" stroke="#4f46e5" dot={false} strokeWidth={2} />
          <Line yAxisId="b" dataKey="conversions" name="Conversions" stroke="#059669" dot={false} strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

const FMT = { cost: ["Spend", money], clicks: ["Clicks", num], impressions: ["Impr.", num], ctr: ["CTR", pct], cpc: ["CPC", money], conversions: ["Conv.", num],
  cpa: ["Cost/conv.", money], conv_value: ["Conv. value", money], wc_orders: ["WC orders", num], wc_revenue: ["WC revenue", money], actual_roas: ["Actual ROAS", x],
  orders: ["Orders", num], revenue: ["Revenue", money], total: ["Total", money], budget: ["Budget", money] };

export function WastedSpend({ w }) {
  const [tab, setTab] = React.useState("all");
  const rows = w.flags.filter((f) => tab === "all" || f.type === tab);
  return (
    <div data-testid="gads-wasted">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-2" data-testid="gads-wasted-total"><div className="text-[11px] uppercase font-semibold text-rose-700">Spend with no orders</div><div className="text-xl font-bold text-rose-800">{gbp(w.wasted_spend)}</div></div>
        <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {[["all", `All (${w.flags.length})`], ["campaign", `Campaigns (${w.counts.campaigns})`], ["search_term", `Search terms (${w.counts.search_terms})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`gads-wasted-tab-${k}`} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${tab === k ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400">Flags campaigns that spent over £{w.campaign_min} and search terms that spent over £{w.term_min}. Suggestions only; nothing is changed in Google Ads.</p>
      </div>
      {!rows.length ? <p className="text-sm text-emerald-700" data-testid="gads-wasted-none">No wasted spend found for this period.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="gads-wasted-table">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left"><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Campaign</th><th className="py-2 pr-3">Why flagged</th>
            <th className="py-2 px-2 text-right">Spend</th><th className="py-2 px-2 text-right">Clicks</th><th className="py-2 px-2 text-right">Conv.</th><th className="py-2 pl-2 text-right">WC revenue</th></tr></thead>
          <tbody>{rows.map((f, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`gads-wasted-row-${i}`}>
              <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${f.level === "error" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{f.type === "campaign" ? "Campaign" : "Search term"}</span></td>
              <td className="py-2 pr-3 font-medium text-slate-800 max-w-[220px] truncate" title={f.name}>{f.name}</td>
              <td className="py-2 pr-3 text-slate-600 max-w-[180px] truncate" title={f.campaign}>{f.type === "campaign" ? "–" : f.campaign}</td>
              <td className="py-2 pr-3 text-xs text-slate-600">{f.reason}</td>
              <td className="py-2 px-2 text-right font-semibold">{gbp(f.cost)}</td><td className="py-2 px-2 text-right">{num(f.clicks)}</td>
              <td className="py-2 px-2 text-right">{num(f.conversions)}</td><td className="py-2 pl-2 text-right">{gbp(f.wc_revenue)}</td>
            </tr>))}</tbody>
        </table></div>
      )}
    </div>
  );
}

export function AdsTable({ rows, cols, mets, testid, empty = "No data for this period." }) {
  if (!rows) return <p className="text-sm text-slate-400">Available once Google Ads is connected.</p>;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left">
          {cols.map(([k, l]) => <th key={k} className="py-2 pr-3 font-semibold">{l}</th>)}{mets.map((m) => <th key={m} className="py-2 px-2 font-semibold text-right">{FMT[m][0]}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {cols.map(([k]) => <td key={k} className="py-2 pr-3 text-slate-700 max-w-[260px] truncate" title={r[k]}>{r[k] ?? "–"}</td>)}
              {mets.map((m) => <td key={m} className="py-2 px-2 text-right">{FMT[m][1](r[m])}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length + mets.length} className="py-3 text-slate-400">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
